/**
 * Google Analytics 4 event tracking.
 *
 * PRIVACY CONTRACT — read before adding an event.
 *
 * The free tools promise "files never leave your device", which is the site's whole
 * differentiator. Events fired from components/free-tools therefore carry ONLY what the
 * user picked from our own UI — which tool, which format pair, how long it took, whether
 * it worked. Never a filename, never a byte count, never dimensions or media duration,
 * never anything from which their file could be described. A size bucket is still a
 * property of their file; it does not belong here either.
 *
 * Nothing anywhere sends an email, a name, or a credit balance. `user_id` is the
 * Supabase UUID, which is a random identifier rather than personal data.
 */

// Push to dataLayer rather than calling window.gtag.
//
// gtag.js loads async (public/index.html), but `window.dataLayer` and the `gtag` shim
// exist synchronously from the inline snippet above it. Reading `window.gtag` meant any
// event fired before the remote script finished was silently dropped — which is exactly
// the fast interactions worth measuring. Pushing to the array queues them instead: gtag
// drains it on load.
const push = (...args) => {
  if (typeof window === 'undefined') return;
  try {
    // Assign only when missing: dataLayer normally exists already (the inline
    // snippet in index.html creates it), and reassigning it unconditionally can
    // throw if anything has made the property read-only. Analytics must never be
    // able to break the page it measures.
    if (!window.dataLayer) window.dataLayer = [];
    window.dataLayer.push(args);
  } catch (err) {
    // Blocked by a privacy extension, a locked-down property, or storage policy.
    // Losing an event is acceptable; throwing out of a click handler is not.
  }
};

const gtag = push;

/* ------------------------------------------------------------------ identity -- */

/**
 * Tie subsequent events to a signed-in user.
 *
 * The Supabase UUID only. Passing an email here would put PII in Google's hands and
 * violates GA's own terms, quite apart from ours.
 */
export const setAnalyticsUser = (userId) => {
  if (!userId) return;
  gtag('set', { user_id: String(userId) });
};

/** Clear identity on logout so the next session is not attributed to the last user. */
export const clearAnalyticsUser = () => {
  gtag('set', { user_id: null });
};

/* ------------------------------------------------------------------- pageview -- */

/**
 * A route change.
 *
 * `path` must be the route PATTERN (/blogs/:slug), not the raw URL: uuids and slugs
 * would otherwise fragment the report into thousands of one-hit rows. The real URL
 * still goes out as page_location.
 */
export const trackPageView = ({ path, location, title }) => {
  gtag('event', 'page_view', {
    page_path: path,
    page_location: location,
    page_title: title,
  });
};

/* --------------------------------------------------------------------- auth -- */

export const trackLogin = (method) => {
  gtag('event', 'login', { method });
};

export const trackSignup = (method) => {
  gtag('event', 'sign_up', { method });
};

export const trackAuthError = (reason) => {
  gtag('event', 'auth_failed', { reason: String(reason || 'unknown').slice(0, 100) });
};

/* ---------------------------------------------------------------- workflows -- */

/**
 * A generation was accepted by the server.
 *
 * Deliberately "started", not "completed": the POST returns as soon as the workflow row
 * exists with status='generating' and the pipeline finishes in a background task, so the
 * client never observes the end. Completion is measurable from the backend, not here.
 */
export const trackWorkflowStart = (type) => {
  gtag('event', 'workflow_started', { workflow_type: type });
};

export const trackWorkflowComplete = (type) => {
  gtag('event', 'workflow_completed', { workflow_type: type });
};

export const trackGenerationFailed = (kind, reason) => {
  gtag('event', 'generation_failed', {
    workflow_type: kind,
    reason: String(reason || 'unknown').slice(0, 100),
  });
};

/** 'cloud' (paid render) vs 'local' (free, in-browser) — the two are not interchangeable. */
export const trackExport = (kind) => {
  gtag('event', 'video_exported', { export_type: kind });
};

export const trackVideoDownload = () => {
  gtag('event', 'video_downloaded');
};

/* --------------------------------------------------------------- free tools -- */

/**
 * A free tool produced a result. See the privacy contract at the top of this file:
 * `pair` is the format conversion the user selected in our UI (e.g. "jpg-to-png"), and
 * `ms` is elapsed wall time. Nothing here describes the user's file.
 */
export const trackToolUsed = (tool, { pair, ms, variant } = {}) => {
  gtag('event', 'tool_used', {
    tool,
    ...(pair ? { format_pair: pair } : null),
    ...(variant ? { variant } : null),
    ...(typeof ms === 'number' ? { duration_ms: Math.round(ms) } : null),
  });
};

export const trackToolError = (tool, reason) => {
  gtag('event', 'tool_failed', {
    tool,
    reason: String(reason || 'unknown').slice(0, 100),
  });
};

/**
 * The ~75MB Whisper model fetch on the subtitle generator — a real dropoff point that
 * happens before the tool can run at all, so a started/completed pair is the only way to
 * see people abandoning during the wait.
 */
export const trackModelDownload = (status) => {
  gtag('event', 'model_download', { status });
};

/* ------------------------------------------------------------ credits & pay -- */

/**
 * Intent to buy, not a purchase.
 *
 * These fire when the user clicks through to Stripe. Payment completes against a
 * server-side webhook that GA never sees, so emitting `purchase` here would count
 * everyone who reached the checkout page as revenue. Real revenue needs a Measurement
 * Protocol call from the webhook.
 */
export const trackBeginCheckout = ({ id, name, price, kind }) => {
  gtag('event', 'begin_checkout', {
    currency: 'USD',
    value: typeof price === 'number' ? price : undefined,
    items: [{ item_id: id, item_name: name, item_category: kind }],
  });
};

export const trackCreditPackageClick = (name, price) => {
  gtag('event', 'credit_package_clicked', { package_name: name, price });
};

export const trackSubscriptionClick = (plan, price) => {
  gtag('event', 'subscription_clicked', { plan_name: plan, price });
};

/* ------------------------------------------------------------ cta & nav -- */

export const trackCTAClick = (name, location) => {
  gtag('event', 'cta_clicked', { cta_name: name, location });
};

export const trackDemoVideoClick = (videoId) => {
  gtag('event', 'demo_video_clicked', { video_id: videoId });
};
